import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { red } from "@mui/material/colors";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import {
  CSSProperties,
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { v4 } from "uuid";
import {
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import HelperText from "./HelperText";
import { PPProps } from "./PPField";

let fontColor: string = "text.secondary";

// TODO: Add Restrictions to this key;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const boxStyle: SxProps = {
  color: "text.secondary",
  mr: 2,
};

const spanStyle: CSSProperties = {};

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

export interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
export interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings: readonly MainTextMatchedSubstrings[];
}
export interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}

const SmartAddressInput: FC<PPProps> = (props) => {
  const stateCtx: StateContextType = useContext(StateContext);
  const { control } = useFormContext();
  const [value, setValue] = useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const loaded = useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }
    loaded.current = true;
  }

  const fetch = useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        200
      ),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Controller
      render={({ field: { onChange, value, name } }) => {
        const isFieldInvalid: boolean =
          stateCtx.applicationContext.fieldState[name] !== undefined &&
          !stateCtx.applicationContext.fieldState[name];
        return (
          <FormControl error={isFieldInvalid} fullWidth>
            <Autocomplete
              id={props.id}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option.structured_formatting.main_text
              }
              filterOptions={(x) => x}
              options={options}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={value}
              onChange={(event: any, newValue: PlaceType | null) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
                onChange(newValue);
                return newValue;
              }}
              isOptionEqualToValue={(
                option: PlaceType | string,
                value: PlaceType
              ) =>
                typeof option === "string"
                  ? true
                  : option.structured_formatting.main_text ===
                    value.structured_formatting.main_text
              }
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <Typography
                      sx={{ color: isFieldInvalid ? red.A700 : fontColor }}
                    >
                      {props.label + (props.required ? " *" : "")}
                    </Typography>
                  }
                  variant="outlined"
                  fullWidth
                />
              )}
              renderOption={(props, option) => {
                const matches =
                  option.structured_formatting.main_text_matched_substrings;
                const parts = parse(
                  option.structured_formatting.main_text,
                  matches.map((match: any) => [
                    match.offset,
                    match.offset + match.length,
                  ])
                );

                return (
                  <li {...props} key={v4()}>
                    <Grid container alignItems="center">
                      <Grid item>
                        <Box component={LocationOnIcon} sx={{ ...boxStyle }} />
                      </Grid>
                      <Grid item xs>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              ...spanStyle,
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                        <Typography variant="body2" color="text.secondary">
                          {option.structured_formatting.secondary_text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
            />
            <FormHelperText>
              {isFieldInvalid ? (
                "Field is required"
              ) : (
                <HelperText helperText={props.helperText}></HelperText>
              )}
            </FormHelperText>
          </FormControl>
        );
      }}
      defaultValue={null}
      name={props.id}
      control={control}
    />
  );
};

export default SmartAddressInput;
//SmartAddressInput.whyDidYouRender = true;
